/**
 * Input sanitization utilities to prevent XSS and injection attacks
 *
 * IMPORTANT: These utilities provide basic sanitization for user inputs.
 * For rendering user-generated HTML content, use a dedicated library like DOMPurify.
 * These functions are designed for sanitizing plain text inputs that should not contain HTML.
 */

/**
 * Sanitize a string by removing HTML tags and potentially dangerous characters
 * This function is designed for plain text inputs that should not contain HTML.
 *
 * USE CASE: User names, addresses, notes - text that should never contain markup
 * NOT FOR: Mathematical expressions, code samples, or formatted text
 *
 * For use cases requiring HTML or mathematical operators, use context-appropriate
 * validation and a dedicated library like DOMPurify for HTML sanitization.
 *
 * @param input - The string to sanitize
 * @returns Sanitized string safe for display
 */
export const sanitizeString = (input: string | null | undefined): string => {
  if (!input) return "";

  // STEP 1: Remove all angle brackets first - this prevents ANY HTML tag/attribute injection
  // Once < and > are removed, no HTML elements or attributes can be formed
  // This makes all subsequent replacements redundant but kept for defense in depth
  // Note: This is intentionally aggressive - inputs requiring < or > should use different validation
  let sanitized = input.replace(/[<>]/g, "");

  // STEP 2: Remove dangerous protocols (redundant after step 1, but defense in depth)
  sanitized = sanitized.replace(/javascript:/gi, "");
  sanitized = sanitized.replace(/data:/gi, "");
  sanitized = sanitized.replace(/vbscript:/gi, "");

  return sanitized.trim();
};

/**
 * Sanitize HTML content by removing all HTML tags
 * WARNING: This function is NOT suitable for allowing safe HTML content.
 * It removes ALL HTML tags to convert to plain text.
 * For allowing safe HTML, use a library like DOMPurify instead.
 * @param input - The HTML content to sanitize
 * @returns Plain text string with all HTML removed
 */
export const sanitizeHTML = (input: string | null | undefined): string => {
  if (!input) return "";

  // First pass: Remove all angle brackets to eliminate ANY HTML tags
  // This is the safest approach - no HTML can exist without < and >
  const sanitized = input.replace(/[<>]/g, "");

  // Note: CodeQL may warn about incomplete sanitization, but removing all angle brackets
  // is actually the most complete way to prevent HTML injection - no HTML can be formed
  // without < and > characters

  return sanitized.trim();
};

/**
 * Sanitize email input
 * @param email - Email to sanitize
 * @returns Sanitized email
 */
export const sanitizeEmail = (email: string | null | undefined): string => {
  if (!email) return "";

  return email
    .toLowerCase()
    .replace(/[<>"']/g, "") // Remove potentially dangerous characters
    .trim();
};

/**
 * Sanitize phone number
 * @param phone - Phone number to sanitize
 * @returns Sanitized phone number with only digits, spaces, hyphens, parentheses, and plus sign
 */
export const sanitizePhone = (phone: string | null | undefined): string => {
  if (!phone) return "";

  // Remove any angle brackets first to prevent HTML injection
  const noAngles = phone.replace(/[<>]/g, "");

  // Keep only digits, spaces, hyphens, parentheses, and plus sign
  return noAngles.replace(/[^0-9()+\-\s]/g, "").trim();
};

/**
 * Sanitize file name to prevent directory traversal
 * @param fileName - File name to sanitize
 * @returns Sanitized file name
 */
export const sanitizeFileName = (fileName: string | null | undefined): string => {
  if (!fileName) return "";

  return fileName
    .replace(/\.\./g, "") // Remove ..
    .replace(/[/\\]/g, "") // Remove path separators
    .replace(/[<>:"|?*]/g, "") // Remove invalid file name characters
    .trim();
};

/**
 * Sanitize numeric input
 * @param input - Input to sanitize
 * @returns Sanitized number or null if invalid
 */
export const sanitizeNumber = (input: string | number | null | undefined): number | null => {
  if (input === null || input === undefined || input === "") return null;

  const num = typeof input === "string" ? parseFloat(input.replace(/[^0-9.-]/g, "")) : input;

  if (!Number.isFinite(num)) return null;

  return num;
};

/**
 * Sanitize metadata object to prevent injection
 * @param metadata - Object to sanitize
 * @returns Sanitized object
 */
export const sanitizeMetadata = (
  metadata: Record<string, any> | null | undefined
): Record<string, string> => {
  if (!metadata || typeof metadata !== "object") return {};

  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(metadata)) {
    // Only allow string keys and values
    if (typeof key === "string" && (typeof value === "string" || typeof value === "number")) {
      sanitized[sanitizeString(key)] = sanitizeString(String(value));
    }
  }

  return sanitized;
};
